import React from 'react';
import { useAnnouncements } from '../hooks/useAnnouncements';
import type { Announcement } from '../hooks/useAnnouncements';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { hasAnyPermission, Permission } from '../utils/roles';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--surface);
  background: var(--primary);
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  text-decoration: none;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 22px rgba(31, 117, 254, 0.32);
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const Title = styled.h3`
  font-size: 1.05rem;
  margin: 0;
  color: var(--onSurface);
`;

const StatusChip = styled.span<{ $status: string | undefined }>`
  background-color: ${({ $status }) =>
    $status === 'published' ? '#d1fae5' : 'var(--outline)'};
  color: ${({ $status }) => ($status === 'published' ? '#059669' : 'var(--onSurfaceVariant)')};
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const Content = styled.p`
  margin: 0.5rem 0 1rem;
  color: #6b7280;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #6b7280;
`;

const AnnouncementImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 0.5rem;
`;

const ExternalLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #2b6cb0;
  font-size: 0.8rem;
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
`;

const EditButton = styled.button`
  border: none;
  background: #eef4ff;
  color: var(--primary);
  padding: 0.35rem 0.75rem;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
`;

// Utility to format time ago
const timeAgo = (dateString: string) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

export const AnnouncementsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageContent = hasAnyPermission(roles, [Permission.MANAGE_CONTENT]);
  const { data, isLoading, error } = useAnnouncements();

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p>Error loading announcements</p>;

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Announcements</HeaderTitle>
        {canManageContent ? (
          <PrimaryButton to="/announcements/new">+ New Announcement</PrimaryButton>
        ) : null}
      </HeaderRow>
      <CardsGrid>
        {data?.map((a: Announcement) => (
          <Card key={a.id}>
            <CardHeader>
              <Title>{a.title}</Title>
              <StatusChip $status={a.status}>{a.status ?? 'draft'}</StatusChip>
            </CardHeader>
            {Array.isArray(a.images) && a.images.length > 0 && (
              <AnnouncementImage src={a.images[0]} alt={a.title} />
            )}
            {a.content && <Content>{a.content}</Content>}
            {a.external_link && (
              <ExternalLink href={a.external_link} target="_blank" rel="noopener noreferrer">
                🔗 {a.external_link}
              </ExternalLink>
            )}
            <Footer>
              <span>{timeAgo(a.created_at)}</span>
              {canManageContent ? (
                <EditButton
                  type="button"
                  onClick={() =>
                    a.source === 'event' && a.source_id
                      ? navigate(`/events/setup/${a.source_id}`)
                      : navigate(`/announcements/${a.source_id ?? a.id}/edit`)
                  }
                >
                  {a.source === 'event' ? 'Open Event' : 'Edit'}
                </EditButton>
              ) : null}
            </Footer>
          </Card>
        ))}
      </CardsGrid>
    </Page>
  );
};

export default AnnouncementsPage;
